import type { Cents } from "../domain/money";
import { formatCents } from "../domain/money";
import {
  ACCOUNTING_DISCLAIMER,
  cell,
  type ExportSheet,
  type ReportIdentity,
  type ReportModel,
  type ReportRow,
  type ReportSection,
  type ReportValue,
} from "./reportModel";

/**
 * TAX / ACCOUNTING RECORDS, MILEAGE AND SOURCE DOCUMENTS (Phase 13, §42F).
 *
 * The accountant-facing side of the application. It reports FACTS — what was
 * spent, on what, with which document behind it — and never states tax
 * treatment or deductibility; that judgement belongs to the accountant.
 *
 * Every row is one recorded transaction or allocation. Where a purchase
 * spans several projects, the allocation is the row, so a project's report
 * never claims another project's money.
 *
 * Pure: the service supplies the rows, this file arranges and exports them.
 */

export type DocumentStatus = "attached" | "missing" | "purchase" | "not-required" | "awaiting-review";

export interface AccountingTransactionRow {
  eventId: string;
  date: string;
  vendor: string;
  description: string;
  amountCents: Cents;
  taxCents: Cents;
  totalCents: Cents;
  bucket: string;
  bucketLabel: string;
  categoryName: string;
  costBasis: string | null;
  sourceKind: "purchase" | "expense" | "mileage" | "return";
  sourceLabel: string;
  documentStatus: DocumentStatus;
  documentName: string | null;
  receiptNumber: string | null;
  featureName: string;
  roomName: string;
  productName: string;
  notes: string;
  purchaseId: string | null;
  expenseId: string | null;
  isReturn: boolean;
}

export interface AccountingPeriod {
  /** Inclusive ISO date, or null for "from the beginning". */
  from: string | null;
  /** Inclusive ISO date, or null for "to today". */
  to: string | null;
  label: string;
}

export const ALL_DATES: AccountingPeriod = { from: null, to: null, label: "All dates" };

export function calendarYear(year: number): AccountingPeriod {
  return { from: `${year}-01-01`, to: `${year}-12-31`, label: `Calendar year ${year}` };
}

export function withinPeriod(date: string, period: AccountingPeriod): boolean {
  if (period.from !== null && date < period.from) return false;
  if (period.to !== null && date > period.to) return false;
  return true;
}

const money = (cents: Cents): ReportValue => ({ kind: "money", cents });
const text = (value: string): ReportValue => ({ kind: "text", text: value });
const empty: ReportValue = { kind: "empty" };

export interface AccountingReportInput {
  identity: ReportIdentity;
  rows: AccountingTransactionRow[];
  period: AccountingPeriod;
  generatedAt: string;
}

/** Bucket → category → transaction, exactly as the accountant reads it. */
export function taxAccountingReport(input: AccountingReportInput): ReportModel {
  const rows = input.rows.filter((row) => withinPeriod(row.date, input.period));
  const buckets = new Map<string, AccountingTransactionRow[]>();
  for (const row of rows) {
    if (!buckets.has(row.bucketLabel)) buckets.set(row.bucketLabel, []);
    buckets.get(row.bucketLabel)!.push(row);
  }

  const sections: ReportSection[] = [];
  for (const [bucketLabel, bucketRows] of buckets) {
    const categories = new Map<string, AccountingTransactionRow[]>();
    for (const row of bucketRows) {
      if (!categories.has(row.categoryName)) categories.set(row.categoryName, []);
      categories.get(row.categoryName)!.push(row);
    }
    const reportRows: ReportRow[] = [];
    for (const [categoryName, categoryRows] of categories) {
      reportRows.push({
        label: categoryName,
        values: [empty, empty, money(total(categoryRows)), empty],
        emphasis: "subtotal",
      });
      for (const row of categoryRows.sort((a, b) => a.date.localeCompare(b.date))) {
        reportRows.push({
          label: `${row.date} · ${row.vendor || "—"} · ${row.description}`,
          note:
            [
              row.sourceLabel,
              row.costBasis ? `${row.costBasis} basis` : "basis not stated",
              row.isReturn ? "return / refund" : null,
              // Spec §60N: unattributed money is stated as such, never left
              // blank — the cost is known, the work item is not.
              row.featureName || "project level — no work item",
              row.roomName || null,
            ]
              .filter(Boolean)
              .join(" · ") || undefined,
          depth: 1,
          values: [money(row.amountCents), money(row.taxCents), money(row.totalCents), text(documentLabel(row.documentStatus))],
          source: row.expenseId
            ? { kind: "expense", id: row.expenseId }
            : row.purchaseId
              ? { kind: "purchase", id: row.purchaseId }
              : undefined,
        });
      }
    }
    reportRows.push({ label: `${bucketLabel} total`, values: [empty, empty, money(total(bucketRows)), empty], emphasis: "total" });
    sections.push({
      title: `${bucketLabel} — ${formatCents(total(bucketRows))}`,
      columnLabel: "Transaction",
      columns: ["Amount", "Sales tax", "Total", "Document"],
      rows: reportRows,
    });
  }

  sections.push({
    title: "All buckets",
    columnLabel: "",
    columns: ["Amount", "Sales tax", "Total", ""],
    rows: [
      {
        label: "Project total for this period",
        values: [
          money(rows.reduce((s, r) => s + r.amountCents, 0)),
          money(rows.reduce((s, r) => s + r.taxCents, 0)),
          money(total(rows)),
          empty,
        ],
        emphasis: "total",
      },
    ],
  });

  const undocumented = rows.filter((row) => row.documentStatus === "missing").length;
  const warnings: string[] = [];
  if (undocumented > 0) {
    warnings.push(`${undocumented} transaction(s) have no source document attached.`);
  }
  const unstatedBasis = rows.filter((row) => row.costBasis === null).length;
  if (unstatedBasis > 0) {
    warnings.push(`${unstatedBasis} transaction(s) do not state a cost basis, so they are reported as mixed rather than guessed.`);
  }

  return {
    id: "tax-accounting-records",
    title: "Tax / accounting records",
    subtitle: input.period.label,
    identity: input.identity,
    optionSummary: input.period.label,
    sections,
    warnings,
    disclosures: [
      "Every row is a recorded purchase allocation, manual expense, mileage entry or return — the same records the rest of the application uses.",
      "Where a purchase covers several projects, only this project's allocation appears here.",
    ],
    disclaimer: ACCOUNTING_DISCLAIMER,
    generatedAt: input.generatedAt,
  };
}

/** One row per transaction/allocation — the accountant's spreadsheet. */
export function accountingExportSheet(
  rows: AccountingTransactionRow[],
  project: { jobNumber: string; projectName: string; address: string; projectType: string },
): ExportSheet {
  return {
    name: "Transactions",
    columns: [
      { key: "job", label: "Job Number" },
      { key: "project", label: "Project Name" },
      { key: "address", label: "Address" },
      { key: "type", label: "Project Type" },
      { key: "date", label: "Date" },
      { key: "bucket", label: "Financial Bucket" },
      { key: "category", label: "Category" },
      { key: "basis", label: "Cost Basis" },
      { key: "vendor", label: "Vendor" },
      { key: "description", label: "Description" },
      { key: "amount", label: "Pre-Tax Amount" },
      { key: "tax", label: "Sales Tax" },
      { key: "total", label: "Total Amount" },
      { key: "record", label: "Record Type" },
      { key: "document", label: "Receipt Attached" },
      { key: "documentName", label: "Source Document" },
      { key: "receiptNumber", label: "Receipt / Invoice Number" },
      { key: "feature", label: "Feature" },
      { key: "room", label: "Room" },
      { key: "product", label: "Product" },
      { key: "notes", label: "Notes" },
    ],
    rows: rows.map((row) => [
      cell.text(project.jobNumber),
      cell.text(project.projectName),
      cell.text(project.address),
      cell.text(project.projectType),
      cell.date(row.date),
      cell.text(row.bucketLabel),
      cell.text(row.categoryName),
      cell.text(row.costBasis ?? "not stated"),
      cell.text(row.vendor),
      cell.text(row.description),
      cell.money(row.amountCents),
      cell.money(row.taxCents),
      cell.money(row.totalCents),
      cell.text(recordTypeLabel(row)),
      cell.text(row.documentStatus === "attached" ? "Yes" : row.documentStatus === "missing" ? "No" : documentLabel(row.documentStatus)),
      cell.text(row.documentName ?? ""),
      cell.text(row.receiptNumber ?? ""),
      cell.text(row.featureName),
      cell.text(row.roomName),
      cell.text(row.productName),
      cell.text(row.notes),
    ]),
  };
}

/* ------------------------------------------------------------------ */
/* Mileage                                                             */
/* ------------------------------------------------------------------ */

export interface MileageReportRow {
  date: string;
  purpose: string;
  startLocation: string;
  destination: string;
  milesX10: number;
  rateCentsPerMile: Cents;
  amountCents: Cents;
  bucketLabel: string;
  notes: string;
}

export function projectMileageReport(input: {
  identity: ReportIdentity;
  rows: MileageReportRow[];
  period: AccountingPeriod;
  generatedAt: string;
}): ReportModel {
  const rows = input.rows.filter((row) => withinPeriod(row.date, input.period));
  const totalMiles = rows.reduce((sum, row) => sum + row.milesX10, 0) / 10;
  const totalAmount = rows.reduce((sum, row) => sum + row.amountCents, 0);
  return {
    id: "mileage",
    title: "Mileage",
    subtitle: input.period.label,
    identity: input.identity,
    optionSummary: input.period.label,
    sections: [
      {
        title: "Trips",
        columnLabel: "Trip",
        columns: ["Miles", "Rate", "Amount"],
        rows: [
          ...rows.map(
            (row): ReportRow => ({
              label: `${row.date} · ${row.purpose || "Trip"}`,
              note:
                [[row.startLocation, row.destination].filter(Boolean).join(" → "), row.bucketLabel, row.notes]
                  .filter(Boolean)
                  .join(" · ") || undefined,
              values: [{ kind: "count", value: row.milesX10 / 10 }, money(row.rateCentsPerMile), money(row.amountCents)],
            }),
          ),
          {
            label: "Total",
            values: [{ kind: "count", value: totalMiles }, empty, money(totalAmount)],
            emphasis: "total",
          },
        ],
      },
    ],
    disclosures: [
      "Each trip stores the mileage rate that was in force when it was recorded. Changing the current rate never rewrites past trips.",
    ],
    disclaimer: ACCOUNTING_DISCLAIMER,
    generatedAt: input.generatedAt,
  };
}

export function mileageExportSheet(
  rows: MileageReportRow[],
  project: { jobNumber: string; projectName: string },
): ExportSheet {
  return {
    name: "Mileage",
    columns: [
      { key: "date", label: "Date" },
      { key: "job", label: "Job Number" },
      { key: "project", label: "Project" },
      { key: "miles", label: "Miles" },
      { key: "rate", label: "Rate (frozen)" },
      { key: "amount", label: "Amount" },
      { key: "bucket", label: "Financial Bucket" },
      { key: "start", label: "Start" },
      { key: "destination", label: "Destination" },
      { key: "purpose", label: "Purpose" },
      { key: "notes", label: "Notes" },
    ],
    rows: rows.map((row) => [
      cell.date(row.date),
      cell.text(project.jobNumber),
      cell.text(project.projectName),
      cell.number(row.milesX10 / 10, 1),
      cell.money(row.rateCentsPerMile),
      cell.money(row.amountCents),
      cell.text(row.bucketLabel),
      cell.text(row.startLocation),
      cell.text(row.destination),
      cell.text(row.purpose),
      cell.text(row.notes),
    ]),
  };
}

/* ------------------------------------------------------------------ */
/* Source document index                                               */
/* ------------------------------------------------------------------ */

export function sourceDocumentIndexReport(input: {
  identity: ReportIdentity;
  rows: AccountingTransactionRow[];
  period: AccountingPeriod;
  generatedAt: string;
}): ReportModel {
  const rows = input.rows.filter((row) => withinPeriod(row.date, input.period));
  const documented = rows.filter((row) => row.documentStatus === "attached");
  const missing = rows.filter((row) => row.documentStatus === "missing");
  const other = rows.filter((row) => row.documentStatus !== "attached" && row.documentStatus !== "missing");

  const build = (title: string, group: AccountingTransactionRow[], note?: string): ReportSection => ({
    title,
    note,
    columnLabel: "Transaction",
    columns: ["Amount", "Status", "File"],
    rows: group
      .sort((a, b) => a.date.localeCompare(b.date))
      .map((row) => ({
        label: `${row.date} · ${row.vendor || "—"} · ${row.description}`,
        note: [row.bucketLabel, row.categoryName, row.receiptNumber ? `#${row.receiptNumber}` : null].filter(Boolean).join(" · "),
        values: [money(row.totalCents), text(documentLabel(row.documentStatus)), text(row.documentName ?? "—")],
        source: row.expenseId
          ? { kind: "expense" as const, id: row.expenseId }
          : row.purchaseId
            ? { kind: "purchase" as const, id: row.purchaseId }
            : undefined,
      })),
  });

  const sections: ReportSection[] = [];
  if (missing.length > 0) {
    sections.push(build("Missing a document", missing, "These transactions are recorded, but nothing backs them up yet."));
  }
  if (documented.length > 0) sections.push(build("Documented", documented));
  if (other.length > 0) sections.push(build("Other statuses", other));
  sections.push({
    title: "Completeness",
    columnLabel: "",
    columns: ["Count", "Amount"],
    rows: [
      { label: "Documented", values: [{ kind: "count", value: documented.length }, money(total(documented))] },
      {
        label: "No document",
        values: [{ kind: "count", value: missing.length }, money(total(missing))],
        emphasis: missing.length > 0 ? "warning" : undefined,
      },
      { label: "All transactions", values: [{ kind: "count", value: rows.length }, money(total(rows))], emphasis: "total" },
    ],
  });

  return {
    id: "source-document-index",
    title: "Source document index",
    subtitle: input.period.label,
    identity: input.identity,
    optionSummary: input.period.label,
    sections,
    warnings:
      missing.length > 0
        ? [`${missing.length} transaction(s) totalling ${formatCents(total(missing))} have no source document.`]
        : [],
    disclosures: [
      "This index lists the documents held for each transaction. It is the map, not the documents themselves — the files stay in the project's managed storage.",
      "Document interpretation is optional: a receipt is fully usable whether or not it was ever read by the document service.",
    ],
    disclaimer: ACCOUNTING_DISCLAIMER,
    generatedAt: input.generatedAt,
  };
}

export function documentIndexExportSheet(rows: AccountingTransactionRow[]): ExportSheet {
  return {
    name: "Documents",
    columns: [
      { key: "date", label: "Date" },
      { key: "vendor", label: "Vendor" },
      { key: "description", label: "Transaction" },
      { key: "amount", label: "Amount" },
      { key: "status", label: "Document Status" },
      { key: "file", label: "Filename" },
      { key: "receipt", label: "Receipt / Invoice Number" },
    ],
    rows: rows.map((row) => [
      cell.date(row.date),
      cell.text(row.vendor),
      cell.text(row.description),
      cell.money(row.totalCents),
      cell.text(documentLabel(row.documentStatus)),
      cell.text(row.documentName ?? ""),
      cell.text(row.receiptNumber ?? ""),
    ]),
  };
}

export function documentLabel(status: DocumentStatus): string {
  switch (status) {
    case "attached":
      return "Attached";
    case "missing":
      return "No document";
    case "purchase":
      return "On the purchase";
    case "awaiting-review":
      return "Awaiting review";
    default:
      return "Not required";
  }
}

function recordTypeLabel(row: AccountingTransactionRow): string {
  if (row.isReturn) return "Return";
  switch (row.sourceKind) {
    case "purchase":
      return "Purchase";
    case "mileage":
      return "Mileage";
    case "return":
      return "Return";
    default:
      return "Expense";
  }
}

function total(rows: AccountingTransactionRow[]): Cents {
  return rows.reduce((sum, row) => sum + row.totalCents, 0);
}
